import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStockIssues, deleteStockIssue, markStockIssueReceived } from '../../redux/actions/stockIssue/stockIssueAction';
import { fetchEmployees } from '../../redux/actions/employee/employeeAction';
import DataTable from 'react-data-table-component';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  Alert,
  CircularProgress
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import ClearIcon from '@mui/icons-material/Clear';
import { confirmDelete, showSuccess } from '../../utils/alertUtils';
import { toast } from 'react-toastify';
import PageLoader from '../common/PageLoader';

const DEFAULT_LIMIT = 200;

const StockIssueTable = ({ currentUser, onRefresh, hideTitle, showReceiveIcon, data, loading: customLoading, hideInlineFilters, employeeOptions, hideEmployeeCode }) => {
  const dispatch = useDispatch();
  const { stockIssues, loading, error } = useSelector((state) => state.stockIssue);
  const { employees } = useSelector((state) => state.employee);
  const { user } = useSelector((state) => state.login);

  // Use custom data if provided, otherwise use from Redux
  const tableData = useMemo(() => Array.isArray(data) ? data : Array.isArray(stockIssues) ? stockIssues : [], [data, stockIssues]);
  const isExternalData = Array.isArray(data);
  const baseLoading = customLoading !== undefined ? customLoading : loading;
  const [hasMore, setHasMore] = useState(!isExternalData);
  const [loadingMore, setLoadingMore] = useState(false);
  const tableLoading = customLoading !== undefined ? customLoading : (loading && !loadingMore);

  // State for filters
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [stockCodeFilter, setStockCodeFilter] = useState('');

  // State for filter modal
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({});

  const offsetRef = useRef(0);
  const paramsRef = useRef({ limit: DEFAULT_LIMIT, today_only: true });

  const loadStockIssues = useCallback(
    async (params = {}, options = {}) => {
      if (isExternalData) {
        return [];
      }

      const append = Boolean(options.append);
      let requestParams;

      if (append) {
        const baseParams = paramsRef.current;
        const limitValue = Number(baseParams.limit ?? DEFAULT_LIMIT);

        if (!limitValue) {
          setHasMore(false);
          return [];
        }

        requestParams = {
          ...baseParams,
          offset: offsetRef.current,
        };
      } else {
        const mergedParams = {
          ...paramsRef.current,
          ...params,
        };

        Object.keys(mergedParams).forEach((key) => {
          if (
            mergedParams[key] === null ||
            mergedParams[key] === undefined ||
            mergedParams[key] === ''
          ) {
            delete mergedParams[key];
          }
        });

        if (params.limit !== undefined) {
          mergedParams.limit = params.limit;
        } else if (mergedParams.limit === undefined) {
          mergedParams.limit = DEFAULT_LIMIT;
        }

        if (params.today_only !== undefined) {
          mergedParams.today_only = params.today_only;
        } else if (mergedParams.today_only === undefined) {
          mergedParams.today_only = true;
        }

        paramsRef.current = mergedParams;
        requestParams = { ...paramsRef.current };
        offsetRef.current = 0;
        setHasMore(Number(requestParams.limit ?? DEFAULT_LIMIT) !== 0);
      }

      try {
        const response = await dispatch(fetchStockIssues(requestParams, { append }));
        const received = Array.isArray(response) ? response.length : 0;

        if (append) {
          offsetRef.current += received;
        } else {
          offsetRef.current = received;
        }

        const limitValue = Number(requestParams.limit ?? DEFAULT_LIMIT);

        if (!limitValue) {
          setHasMore(false);
        } else {
          setHasMore(received === limitValue);
        }

        return response;
      } catch (err) {
        setHasMore(false);
        return [];
      }
    },
    [dispatch, isExternalData]
  );

  useEffect(() => {
    if (!isExternalData) {
      loadStockIssues({ limit: DEFAULT_LIMIT, today_only: true });
    }
    if (!employeeOptions) {
      dispatch(fetchEmployees());
    }
  }, [dispatch, isExternalData, loadStockIssues, employeeOptions]);

  useEffect(() => {
    if (isExternalData) {
      setHasMore(false);
    }
  }, [isExternalData]);

  const handleReceive = async (id) => {
    try {
      await dispatch(markStockIssueReceived(id, currentUser?.username));
      showSuccess('Received!', 'The stock issue has been marked as received.');
      if (onRefresh) onRefresh();
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to receive stock issue';
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    const result = await confirmDelete('Are you sure?', 'This stock issue record will be deleted permanently!');
    if (result.isConfirmed) {
      try {
        await dispatch(deleteStockIssue(id));
        showSuccess('Deleted!', 'The stock issue record has been deleted.');
        if (onRefresh) onRefresh();
              } catch (error) {
          const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete stock issue record';
          toast.error(errorMessage);
        }
    }
  };

  // Calculate status counts
  const statusCounts = useMemo(() => {
    return tableData.reduce((acc, item) => {
      acc[item.status] = (acc[item.status] || 0) + 1;
      return acc;
    }, {});
  }, [tableData]);

  // Filter data based on search and filters
  const filteredData = useMemo(() => {
    let filtered = tableData;

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(item =>
        (item.stock_code && item.stock_code.toLowerCase().includes(searchLower)) ||
        (item.stock_name && item.stock_name.toLowerCase().includes(searchLower)) ||
        (item.emp_code && item.emp_code.toLowerCase().includes(searchLower)) ||
        (item.batch_no && item.batch_no.toLowerCase().includes(searchLower))
      );
    }

    if (fromDate) {
      filtered = filtered.filter(item => new Date(item.created_at) >= new Date(fromDate));
    }

    if (toDate) {
      filtered = filtered.filter(item => new Date(item.created_at) <= new Date(toDate + 'T23:59:59'));
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    if (selectedEmployee) {
      filtered = filtered.filter(item => item.emp_code === selectedEmployee.emp_code);
    }

    if (stockCodeFilter) {
      filtered = filtered.filter(item => 
        item.stock_code && item.stock_code.toLowerCase().includes(stockCodeFilter.toLowerCase())
      );
    }

    return filtered;
  }, [tableData, search, fromDate, toDate, statusFilter, selectedEmployee, stockCodeFilter]);

  const handleClearFilters = async () => {
    setSearch('');
    setFromDate('');
    setToDate('');
    setStatusFilter('all');
    setSelectedEmployee(null);
    setStockCodeFilter('');
    if (!isExternalData) {
      await loadStockIssues({ limit: DEFAULT_LIMIT, today_only: true });
    }
  };

  const handleApplyFilters = () => {
    setFromDate(tempFilters.fromDate || '');
    setToDate(tempFilters.toDate || '');
    setSelectedEmployee(tempFilters.selectedEmployee || null);
    setStockCodeFilter(tempFilters.stockCodeFilter || '');
    setFilterModalOpen(false);
  };

  const handleOpenFilterModal = () => {
    setTempFilters({
      fromDate,
      toDate,
      selectedEmployee,
      stockCodeFilter
    });
    setFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setFilterModalOpen(false);
  };

  const handleExport = () => {
    const csvRows = [
      ['Weight Processed', 'Stock Code', 'Stock Name', 'Employee Code', 'Quantity', 'Weight', 'Batch No', 'Status', 'Created At', 'Received By', 'Received At'],
      ...filteredData.map(row => [
        row.is_processed_in_batch ? 'Processed' : 'Not Processed',
        row.stock_code || '',
        row.stock_name || '',
        row.emp_code || '',
        row.qty || '',
        row.weight || '',
        row.batch_no || '',
        row.status || '',
        row.created_at ? new Date(row.created_at).toLocaleString() : '',
        row.received_by || '',
        row.received_at ? new Date(row.received_at).toLocaleString() : ''
      ])
    ];
    const csvContent = csvRows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'stock_issues.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleLoadRecent = async () => {
    if (isExternalData) return;
    await loadStockIssues({ limit: DEFAULT_LIMIT, today_only: true });
  };

  const handleLoadAll = async () => {
    if (isExternalData) return;
    await loadStockIssues({ limit: 0, today_only: false });
  };

  const handleLoadMore = async () => {
    if (isExternalData) return;
    setLoadingMore(true);
    try {
      await loadStockIssues({}, { append: true });
    } finally {
      setLoadingMore(false);
    }
  };

  const showServerControls = !isExternalData;

  const columns = [
    { 
      name: 'Weight Processed', 
      selector: (row) => row.is_processed_in_batch || 0, 
      sortable: true, 
      width: '140px',
      cell: (row) => (
        <span style={{
          display: 'inline-block',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '0.75rem',
          fontWeight: 500,
          backgroundColor: row.is_processed_in_batch ? '#4caf50' : '#ff9800',
          color: 'white',
          textAlign: 'center',
          minWidth: '80px'
        }}>
          {row.is_processed_in_batch ? 'Processed' : 'Not Processed'}
        </span>
      )
    },
    { name: 'Stock Code', selector: (row) => row.stock_code, sortable: true, width: '150px' },
    { name: 'Stock Name', selector: (row) => row.stock_name, sortable: true, width: '200px' },
    ...(!hideEmployeeCode ? [{ name: 'Employee Code', selector: (row) => row.emp_code, sortable: true, width: '150px' }] : []),
    { name: 'Quantity', selector: (row) => row.qty, sortable: true, width: '120px' },
    { name: 'Weight', selector: (row) => row.weight, sortable: true, width: '120px' },
    { name: 'Batch No', selector: (row) => row.batch_no || '-', sortable: true, width: '150px' },
    { name: 'Created At', selector: (row) => new Date(row.created_at).toLocaleString(), sortable: true, width: '180px' },
    { name: 'Status', cell: (row) => (
      <span style={{
        display: 'inline-block',
        border: '1px solid #1976d2',
        color: '#1976d2',
        borderRadius: '16px',
        padding: '2px 12px',
        fontSize: '0.875rem',
        fontWeight: 500,
        background: row.status === 'received' ? '#e8f5e9' : '#fff3e0',
        minWidth: 32,
        textAlign: 'center',
      }}>{row.status}</span>
    ), ignoreRowClick: true, width: '120px' },
    { name: 'Received By', selector: (row) => row.received_by || '-', sortable: true, width: '150px' },
    { name: 'Received At', selector: (row) => row.received_at ? new Date(row.received_at).toLocaleString() : '-', sortable: true, width: '180px' },
    // Show Actions column for Admin and User roles (receive for both, delete only for Admin)
    ...((user?.role === 'Admin' || user?.role === 'User') ? [{
      name: 'Actions',
      cell: (row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          {showReceiveIcon && row.status === 'issued' && handleReceive && (
            <IconButton size="small" color="success" onClick={() => handleReceive(row.id)}>
              <CheckIcon />
            </IconButton>
          )}
          {user?.role === 'Admin' && handleDelete && (
            <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      width: '120px'
    }] : []),
  ];

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <PageLoader loading={showServerControls && baseLoading && !loadingMore} />
      {!hideTitle && (
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
          Stock Issue List
        </Typography>
      )}
      
      {/* Compact Filters Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              fullWidth
              placeholder="Search stock code, name, employee..."
            />
          </Grid>
          {!hideInlineFilters && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="issued">
                    Pending ({statusCounts.issued})
                  </MenuItem>
                  <MenuItem value="received">
                    Received ({statusCounts.received})
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          {!hideInlineFilters && (
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                onClick={handleOpenFilterModal}
                size="small"
                startIcon={<FilterListIcon />}
                fullWidth
              >
                Filters
              </Button>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="outlined"
              onClick={handleExport}
              size="small"
              startIcon={<DownloadIcon />}
              fullWidth
            >
              Export
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleClearFilters}
              size="small"
              startIcon={<ClearIcon />}
              fullWidth
            >
              Clear
            </Button>
          </Grid>
          {showServerControls && (
            <>
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  variant="outlined"
                  onClick={handleLoadRecent}
                  size="small"
                  fullWidth
                >
                  Load Recent
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  variant="outlined"
                  onClick={handleLoadAll}
                  size="small"
                  fullWidth
                >
                  Load All
                </Button>
              </Grid>
            </>
          )}
        </Grid>
        
        {/* Active Filters Chips */}
        {(search || fromDate || toDate || statusFilter !== 'all' || selectedEmployee || stockCodeFilter) && (
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {search && (
              <span style={{
                display: 'inline-block',
                border: '1px solid #1976d2',
                color: '#1976d2',
                borderRadius: '16px',
                padding: '2px 12px',
                fontSize: '0.875rem',
                fontWeight: 500,
                background: '#e3f2fd',
                minWidth: 32,
                textAlign: 'center',
                marginRight: 4
              }}>{`Search: "${search}"`}</span>
            )}
            {fromDate && (
              <span style={{
                display: 'inline-block',
                border: '1px solid #1976d2',
                color: '#1976d2',
                borderRadius: '16px',
                padding: '2px 12px',
                fontSize: '0.875rem',
                fontWeight: 500,
                background: '#e3f2fd',
                minWidth: 32,
                textAlign: 'center',
                marginRight: 4
              }}>{`From: ${fromDate}`}</span>
            )}
            {toDate && (
              <span style={{
                display: 'inline-block',
                border: '1px solid #1976d2',
                color: '#1976d2',
                borderRadius: '16px',
                padding: '2px 12px',
                fontSize: '0.875rem',
                fontWeight: 500,
                background: '#e3f2fd',
                minWidth: 32,
                textAlign: 'center',
                marginRight: 4
              }}>{`To: ${toDate}`}</span>
            )}
            {statusFilter !== 'all' && (
              <span style={{
                display: 'inline-block',
                border: '1px solid #1976d2',
                color: '#1976d2',
                borderRadius: '16px',
                padding: '2px 12px',
                fontSize: '0.875rem',
                fontWeight: 500,
                background: '#e3f2fd',
                minWidth: 32,
                textAlign: 'center',
                marginRight: 4
              }}>{`Status: ${statusFilter}`}</span>
            )}
            {selectedEmployee && (
              <span style={{
                display: 'inline-block',
                border: '1px solid #1976d2',
                color: '#1976d2',
                borderRadius: '16px',
                padding: '2px 12px',
                fontSize: '0.875rem',
                fontWeight: 500,
                background: '#e3f2fd',
                minWidth: 32,
                textAlign: 'center',
                marginRight: 4
              }}>{`Employee: ${selectedEmployee.emp_code}`}</span>
            )}
            {stockCodeFilter && (
              <span style={{
                display: 'inline-block',
                border: '1px solid #1976d2',
                color: '#1976d2',
                borderRadius: '16px',
                padding: '2px 12px',
                fontSize: '0.875rem',
                fontWeight: 500,
                background: '#e3f2fd',
                minWidth: 32,
                textAlign: 'center',
                marginRight: 4
              }}>{`Stock Code: "${stockCodeFilter}"`}</span>
            )}
          </Box>
        )}
      </Paper>

      {/* Filter Modal */}
      <Modal
        open={filterModalOpen}
        onClose={handleCloseFilterModal}
        aria-labelledby="filter-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
        }}>
          <Typography id="filter-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
            Advanced Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="From Date"
                type="date"
                value={tempFilters.fromDate || ''}
                onChange={(e) => setTempFilters({...tempFilters, fromDate: e.target.value})}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="To Date"
                type="date"
                value={tempFilters.toDate || ''}
                onChange={(e) => setTempFilters({...tempFilters, toDate: e.target.value})}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Employee</InputLabel>
                <Select
                  value={tempFilters.selectedEmployee?.id || ''}
                  label="Employee"
                  onChange={(e) => {
                    const employee = (employeeOptions || employees).find(emp => emp.id === e.target.value);
                    setTempFilters({...tempFilters, selectedEmployee: employee || null});
                  }}
                >
                  <MenuItem value="">All Employees</MenuItem>
                  {(employeeOptions || employees).map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {employee.emp_code} - {employee.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Stock Code"
                value={tempFilters.stockCodeFilter || ''}
                onChange={(e) => setTempFilters({...tempFilters, stockCodeFilter: e.target.value})}
                fullWidth
                size="small"
                placeholder="Filter by stock code..."
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={handleCloseFilterModal}>Cancel</Button>
            <Button onClick={handleApplyFilters} variant="contained">Apply</Button>
          </Box>
        </Box>
      </Modal>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading data: {error}
        </Alert>
      )}

      <DataTable
        columns={columns}
        data={filteredData}
        pagination
        highlightOnHover
        dense
        responsive
        noHeader
        progressPending={tableLoading}
        progressComponent={<CircularProgress />}
      />
      {showServerControls && hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            onClick={handleLoadMore}
            disabled={baseLoading || loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Load More'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default StockIssueTable; 